import { useQuery } from '@tanstack/react-query';
import { queryKeys, unwrap, type components } from '@geefamily/api';
import { api } from '@/app/api';

export type Story = components['schemas']['StoryResponse'];
export type StoryFrame = components['schemas']['StoryFrameResponse'];

const keys = queryKeys.module('catalog').entity('stories');

/**
 * Every story that is live right now.
 *
 * One query serves both jobs: deciding which tiles get a lit ring, and filling
 * the viewer the moment one is tapped. A second request at that moment would be
 * a spinner on a full-screen image.
 *
 * Stories start and end on a schedule, so this is refetched far more often than
 * the site list — which changes only when an editor touches it.
 */
export function useLiveStories() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: () => unwrap<Story[]>(api.GET('/api/v1/catalog/stories', {})),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    // Refetching underneath an open viewer would swap frames mid-story.
    refetchOnWindowFocus: false,
  });
}

/**
 * Drops anything whose window closed since the response was cached, so a ring
 * goes dark on time rather than at the next refetch.
 */
export function selectLive(stories: Story[] | undefined, now = Date.now()): Story[] {
  return (stories ?? []).filter((story) => new Date(story.expiresAtUtc).getTime() > now);
}
